import * as React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/layout/Logo';

export function Login(): JSX.Element {
  const { t } = useTranslation();
  const { credentials, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  if (credentials) {
    return <Navigate to="/" replace />;
  }

  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    try {
      await login({ email, password });
      navigate(from, { replace: true });
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : null;
      if (status === 401) {
        toast.error(t('auth.invalidCreds'));
      } else if (status === undefined) {
        toast.error(t('auth.backendUnreachable'));
      } else {
        toast.error(t('auth.errorStatus', { status }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-40 -right-24 h-[32rem] w-[32rem] rounded-full bg-violet-500/20 blur-3xl" />
        <div className="absolute inset-0 [background-image:radial-gradient(hsl(var(--border))_1px,transparent_1px)] [background-size:24px_24px] opacity-40" />
      </div>

      <div className="relative z-10 w-full max-w-sm animate-fade-in-up">
        <div className="mb-6 flex flex-col items-center text-center">
          <Logo className="h-10" />
          <p className="mt-4 text-sm text-muted-foreground">{t('auth.subtitle')}</p>
        </div>
        <Card className="w-full shadow-xl">
          <CardHeader className="sr-only">
            <CardTitle>{t('auth.title')}</CardTitle>
            <CardDescription>{t('auth.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                autoFocus
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting || !email || !password}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('auth.verifying')}
                </>
              ) : (
                t('auth.login')
              )}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              <Trans
                i18nKey="auth.credsHint"
                components={{ storage: <code className="rounded bg-muted px-1" /> }}
              />
            </p>
          </form>
          </CardContent>
        </Card>
        <p className="mt-6 text-center text-xs text-muted-foreground/70">
          Zorro AI · Console ops
        </p>
      </div>
    </div>
  );
}
